/**
 * Command Palette (Cmd+K)
 *
 * Provides quick navigation and actions via keyboard shortcut.
 * Uses cmdk library for the command palette UI.
 *
 * Features:
 * - Global navigation commands
 * - Client-specific commands (when client is selected)
 * - Admin commands (for admin users only)
 * - Quick client switching
 * - Permission and feature flag aware
 *
 * Can be opened programmatically via useCommandPalette() hook.
 */

import { Command } from "cmdk";
import { Building2, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useClient } from "@/context/ClientContext";
import { useCommandPalette } from "@/context/CommandPaletteContext";
import { useMode } from "@/context/ModeContext";
import { useCommandPaletteCommands } from "@/hooks/useCommandPaletteCommands";
import { useCanAccessAdmin } from "@/permissions";
import { clientNameToSlug, getClientSlug } from "@/types/client";
import { buildWorkspacePath } from "@/utils/routes";

export function CommandPalette() {
  const { isOpen, close, toggle } = useCommandPalette();
  const [search, setSearch] = useState("");
  const [showClientSwitcher, setShowClientSwitcher] = useState(false);
  const navigate = useNavigate();
  const { currentClient, clients, setCurrentClient } = useClient();
  const { startBuild } = useMode();
  const canAccessAdmin = useCanAccessAdmin();

  const clientId = currentClient?.id;
  const clientSlug = currentClient ? getClientSlug(currentClient) : null;

  // Toggle the command palette with Cmd+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggle();
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [toggle]);

  // Reset state when palette closes
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setShowClientSwitcher(false);
    }
  }, [isOpen]);

  const closeAndNavigate = useCallback(
    (path: string) => {
      close();
      setSearch("");
      setShowClientSwitcher(false);
      void navigate(path);
    },
    [navigate, close],
  );

  const handleClientSwitch = useCallback(
    (client: { id: string; name: string; slug?: string | null }) => {
      setCurrentClient(client.id);
      // The stored slug, not one derived from the name: a name-derived slug
      // that does not exist cleared the current client (navigation_audit F8).
      const slug = client.slug || clientNameToSlug(client.name);
      closeAndNavigate(buildWorkspacePath(slug));
    },
    [setCurrentClient, closeAndNavigate],
  );

  // Use route builder to get flag-aware workspace path
  const clientPath = clientSlug ? buildWorkspacePath(clientSlug) : "";

  // Build commands list with permissions
  const commands = useCommandPaletteCommands({
    clientId,
    clientSlug,
    clientPath,
    canAccessAdmin,
    closeAndNavigate,
    close,
    startBuild,
    setShowClientSwitcher,
  });

  if (!isOpen) return null;

  // Client switcher sub-palette
  if (showClientSwitcher) {
    return (
      <div className="fixed inset-0 z-50">
        <div className="fixed inset-0 bg-black/50" onClick={close} />
        <div className="fixed left-1/2 top-[20%] w-full max-w-lg -translate-x-1/2">
          <Command
            className="rounded-xl border border-gray-200 bg-white shadow-2xl"
            shouldFilter={true}
          >
            <div className="flex items-center border-b border-gray-200 px-4">
              <button
                onClick={() => setShowClientSwitcher(false)}
                className="mr-2 text-gray-400 hover:text-gray-600"
              >
                ←
              </button>
              <Search className="mr-2 h-4 w-4 shrink-0 text-gray-400" />
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder="Search clients..."
                className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-gray-400"
                autoFocus
              />
              <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border border-gray-200 bg-gray-100 px-1.5 font-mono text-[10px] font-medium text-gray-500">
                ESC
              </kbd>
            </div>

            <Command.List className="max-h-80 overflow-y-auto p-2">
              <Command.Empty className="py-6 text-center text-sm text-gray-500">
                No clients found.
              </Command.Empty>

              <Command.Group
                heading="Switch Workspace"
                className="px-2 py-1.5 text-xs font-semibold text-gray-500"
              >
                {clients.map((client) => (
                  <Command.Item
                    key={client.id}
                    value={client.name}
                    onSelect={() => handleClientSwitch(client)}
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 aria-selected:bg-gray-100"
                  >
                    <Building2 className="h-4 w-4 text-gray-400" />
                    <span>{client.name}</span>
                    {client.id === currentClient?.id && (
                      <span className="ml-auto text-xs text-green-600">
                        Current
                      </span>
                    )}
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>

            <div className="border-t border-gray-200 px-4 py-2">
              <p className="text-xs text-gray-400">
                <kbd className="rounded bg-gray-100 px-1">↑↓</kbd> to navigate,{" "}
                <kbd className="rounded bg-gray-100 px-1">Enter</kbd> to switch
              </p>
            </div>
          </Command>
        </div>
      </div>
    );
  }

  // Main command palette
  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={close} />

      {/* Command palette */}
      <div className="fixed left-1/2 top-[20%] w-full max-w-lg -translate-x-1/2">
        <Command
          className="rounded-xl border border-gray-200 bg-white shadow-2xl"
          shouldFilter={true}
        >
          <div className="flex items-center border-b border-gray-200 px-4">
            <Search className="mr-2 h-4 w-4 shrink-0 text-gray-400" />
            <Command.Input
              value={search}
              onValueChange={setSearch}
              placeholder={
                clientId && currentClient
                  ? `Search in ${currentClient.name}...`
                  : "Search Nexus..."
              }
              className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-gray-400"
            />
            <kbd className="pointer-events-none ml-2 inline-flex h-5 select-none items-center gap-1 rounded border border-gray-200 bg-gray-100 px-1.5 font-mono text-[10px] font-medium text-gray-500">
              ESC
            </kbd>
          </div>

          <Command.List className="max-h-80 overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-gray-500">
              No results found.
            </Command.Empty>

            {/* Workspace group - show first when client is selected (more relevant context) */}
            {clientId && (
              <Command.Group
                heading={`Workspace: ${currentClient?.name ?? "Selected"}`}
                className="px-2 py-1.5 text-xs font-semibold text-gray-500"
              >
                {commands
                  .filter((c) => c.category === "workspace")
                  .map((command) => (
                    <Command.Item
                      key={command.id}
                      value={`${command.label} ${command.keywords?.join(" ") ?? ""}`}
                      onSelect={command.action}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 aria-selected:bg-gray-100"
                    >
                      <span className="text-gray-400">{command.icon}</span>
                      <span>{command.label}</span>
                    </Command.Item>
                  ))}
              </Command.Group>
            )}

            {/* Platform group */}
            <Command.Group
              heading="Platform"
              className="px-2 py-1.5 text-xs font-semibold text-gray-500"
            >
              {commands
                .filter((c) => c.category === "platform")
                .map((command) => (
                  <Command.Item
                    key={command.id}
                    value={`${command.label} ${command.keywords?.join(" ") ?? ""}`}
                    onSelect={command.action}
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 aria-selected:bg-gray-100"
                  >
                    <span className="text-gray-400">{command.icon}</span>
                    <span>{command.label}</span>
                    {command.shortcut && (
                      <kbd className="ml-auto text-xs text-gray-400">
                        {command.shortcut}
                      </kbd>
                    )}
                  </Command.Item>
                ))}
            </Command.Group>

            {/* Admin group (only show for admin users) */}
            {canAccessAdmin && (
              <Command.Group
                heading="Administration"
                className="px-2 py-1.5 text-xs font-semibold text-gray-500"
              >
                {commands
                  .filter((c) => c.category === "admin")
                  .map((command) => (
                    <Command.Item
                      key={command.id}
                      value={`${command.label} ${command.keywords?.join(" ") ?? ""}`}
                      onSelect={command.action}
                      className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 aria-selected:bg-gray-100"
                    >
                      <span className="text-gray-400">{command.icon}</span>
                      <span>{command.label}</span>
                    </Command.Item>
                  ))}
              </Command.Group>
            )}

            {/* Actions group */}
            <Command.Group
              heading="Actions"
              className="px-2 py-1.5 text-xs font-semibold text-gray-500"
            >
              {commands
                .filter((c) => c.category === "actions")
                .map((command) => (
                  <Command.Item
                    key={command.id}
                    value={`${command.label} ${command.keywords?.join(" ") ?? ""}`}
                    onSelect={command.action}
                    className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 aria-selected:bg-gray-100"
                  >
                    <span className="text-gray-400">{command.icon}</span>
                    <span>{command.label}</span>
                  </Command.Item>
                ))}
            </Command.Group>
          </Command.List>

          <div className="border-t border-gray-200 px-4 py-2">
            <p className="text-xs text-gray-400">
              <kbd className="rounded bg-gray-100 px-1">↑↓</kbd> to navigate,{" "}
              <kbd className="rounded bg-gray-100 px-1">Enter</kbd> to select,{" "}
              <kbd className="rounded bg-gray-100 px-1">Esc</kbd> to close
            </p>
          </div>
        </Command>
      </div>
    </div>
  );
}
